import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit, FiMail, FiPhone, FiMapPin, FiCalendar } from 'react-icons/fi';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import './StudentDetail.css';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStudent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/students/${id}?t=${new Date().getTime()}`);
      setStudent(res.data.data);
    } catch (error) {
      toast.error('Gagal mengambil data mahasiswa');
      navigate('/students');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchStudent();
  }, [fetchStudent, location.key]);

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (!student) {
    return null;
  }

  const getStatusBadge = (status) => {
    const badges = {
      'Aktif': 'badge-success',
      'Cuti': 'badge-warning',
      'Alumni': 'badge-info',
      'Non-Aktif': 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  return (
    <div className="student-detail-page">
      <div className="page-header">
        <button className="btn btn-outline" onClick={() => navigate('/students')}>
          <FiArrowLeft /> Kembali
        </button>
        <Link to={`/students/edit/${id}`} className="btn btn-primary">
          <FiEdit /> Edit
        </Link>
      </div>

      <div className="detail-grid">
        {/* Profile Card */}
        <div className="card profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              <img 
                key={student.photo || 'default'}
                src={student.photo && student.photo !== 'default-avatar.png' 
                  ? `/uploads/students/${student.photo}?t=${new Date().getTime()}` 
                  : '/default-avatar.png'
                } 
                alt="Avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-avatar.png';
                }}
              />
            </div>
            <h2>{student.name}</h2>
            <p className="nim">{student.nim}</p>
            <span className={`badge ${getStatusBadge(student.status)}`}>
              {student.status}
            </span>
          </div>

          <div className="profile-info">
            <div className="info-item">
              <FiMail />
              <div>
                <label>Email</label>
                <p>{student.email}</p>
              </div>
            </div>

            <div className="info-item">
              <FiPhone />
              <div>
                <label>Telepon</label>
                <p>{student.phone}</p>
              </div>
            </div>

            <div className="info-item">
              <FiMapPin />
              <div>
                <label>Alamat</label>
                <p>{student.address}</p>
              </div>
            </div>

            <div className="info-item">
              <FiCalendar />
              <div>
                <label>Tanggal Lahir</label>
                <p>{format(new Date(student.dateOfBirth), 'dd MMMM yyyy', { locale: localeId })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Education History */}
        {student.riwayat_pendidikan && student.riwayat_pendidikan.length > 0 && (
          <div className="card education-history-card">
            <h3>Riwayat Pendidikan</h3>
            <div className="education-timeline">
              {student.riwayat_pendidikan.map((riwayat, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <h4>{riwayat.nama_program_studi}</h4>
                      {riwayat.angkatan && (
                        <span className="badge badge-primary">Angkatan {riwayat.angkatan}</span>
                      )}
                    </div>
                    
                    <div className="timeline-details">
                      <div className="detail-row">
                        <label>NIM:</label>
                        <span>{riwayat.nim}</span>
                      </div>
                      
                      {riwayat.nama_periode_masuk && (
                        <div className="detail-row">
                          <label>Periode Masuk:</label>
                          <span>{riwayat.nama_periode_masuk}</span>
                        </div>
                      )}
                      
                      <div className="detail-row">
                        <label>Jenis Pendaftaran:</label>
                        <span>{riwayat.nama_jenis_daftar || '-'}</span>
                      </div>
                      
                      <div className="detail-row">
                        <label>Jalur Masuk:</label>
                        <span>{riwayat.nama_jalur_masuk || '-'}</span>
                      </div>
                      
                      {riwayat.tanggal_daftar && (
                        <div className="detail-row">
                          <label>Tanggal Daftar:</label>
                          <span>{format(new Date(riwayat.tanggal_daftar), 'dd MMMM yyyy', { locale: localeId })}</span>
                        </div>
                      )}
                      
                      {riwayat.nama_perguruan_tinggi_asal && (
                        <>
                          <div className="detail-row">
                            <label>PT Asal:</label>
                            <span>{riwayat.nama_perguruan_tinggi_asal}</span>
                          </div>
                          {riwayat.nama_prodi_asal && (
                            <div className="detail-row">
                              <label>Prodi Asal:</label>
                              <span>{riwayat.nama_prodi_asal}</span>
                            </div>
                          )}
                          {riwayat.sks_diakui > 0 && (
                            <div className="detail-row">
                              <label>SKS Diakui:</label>
                              <span className="badge badge-success">{riwayat.sks_diakui} SKS</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {riwayat.nama_jenis_keluar && (
                        <>
                          <div className="detail-row">
                            <label>Status Keluar:</label>
                            <span className="badge badge-warning">{riwayat.nama_jenis_keluar}</span>
                          </div>
                          {riwayat.tanggal_keluar && (
                            <div className="detail-row">
                              <label>Tanggal Keluar:</label>
                              <span>{format(new Date(riwayat.tanggal_keluar), 'dd MMMM yyyy', { locale: localeId })}</span>
                            </div>
                          )}
                          {riwayat.keterangan_keluar && (
                            <div className="detail-row">
                              <label>Keterangan:</label>
                              <span>{riwayat.keterangan_keluar}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetail;
